<template>
  <div class="dialog-overlay" v-if="show">
    <div class="dialog-container">
      <div class="dialog-header">
        <h3 class="dialog-title">Geolocation Restrictions</h3>
        <button @click="$emit('close')" class="close-button" title="Close">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="close-icon">
            <path d="M18 6L6 18M6 6l12 12"/>
          </svg>
        </button>
      </div>
      
      <div class="dialog-content">
        <div class="selected-count" v-if="selectedLessons.length > 0">
          <span class="count">{{ selectedLessons.length }}</span>
          <span class="text">lessons selected</span>
        </div>
        
        <div class="info-section">
          <div class="info-icon">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <circle cx="12" cy="12" r="10"></circle>
              <line x1="12" y1="16" x2="12" y2="12"></line>
              <line x1="12" y1="8" x2="12.01" y2="8"></line>
            </svg>
          </div>
          <p class="info-text">
            Set geolocation restrictions to control where these lessons can be accessed from.
            Lessons will only be viewable in the allowed countries or regions.
          </p>
        </div>
        
        <div class="form-group">
          <div class="toggle-container">
            <label class="toggle-label">Enable Geolocation Restrictions</label>
            <label class="switch">
              <input type="checkbox" v-model="form.enabled">
              <span class="slider"></span>
            </label>
          </div>
        </div>
        
        <div class="form-group" v-if="form.enabled">
          <label class="form-label">Restriction Type</label>
          <div class="radio-group">
            <label class="radio-label">
              <input type="radio" v-model="form.restrictionType" value="allowlist">
              <span>Allow only from selected locations</span>
            </label>
            <label class="radio-label">
              <input type="radio" v-model="form.restrictionType" value="blocklist">
              <span>Block from selected locations</span>
            </label>
          </div>
        </div>
        
        <div class="form-group" v-if="form.enabled">
          <label for="countries" class="form-label">{{ form.restrictionType === 'allowlist' ? 'Allowed' : 'Blocked' }} Countries</label>
          <div class="country-select">
            <select 
              multiple
              v-model="form.countries"
              class="form-select"
              id="countries"
            >
              <option v-for="country in availableCountries" :key="country.code" :value="country.code">
                {{ country.name }}
              </option>
            </select>
          </div>
          <p class="helper-text">You can select multiple countries by holding Ctrl (or Cmd on Mac) and clicking.</p>
        </div>
        
        <div class="form-group" v-if="form.enabled">
          <label class="form-label">IP Address Ranges (Optional)</label>
          <div class="ip-ranges">
            <div 
              v-for="(range, index) in form.ipRanges" 
              :key="index"
              class="ip-range-item"
            >
              <input 
                type="text" 
                v-model="range.start" 
                placeholder="Start IP (e.g. 192.168.1.1)"
                class="form-input ip-input"
              >
              <span class="ip-separator">to</span>
              <input 
                type="text" 
                v-model="range.end" 
                placeholder="End IP (e.g. 192.168.1.255)"
                class="form-input ip-input"
              >
              <button 
                @click="removeIpRange(index)" 
                class="remove-ip-button"
                title="Remove IP Range"
              >
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="remove-icon">
                  <path d="M18 6L6 18M6 6l12 12"/>
                </svg>
              </button>
            </div>
            <button @click="addIpRange" class="add-ip-button">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="add-icon">
                <line x1="12" y1="5" x2="12" y2="19"></line>
                <line x1="5" y1="12" x2="19" y2="12"></line>
              </svg>
              Add IP Range
            </button>
          </div>
        </div>
        
        <div class="form-group" v-if="form.enabled">
          <label class="form-label">Show Message to Blocked Users</label>
          <textarea 
            v-model="form.blockMessage" 
            class="form-textarea"
            placeholder="This content is not available in your region."
            rows="3"
          ></textarea>
        </div>
      </div>
      
      <div class="dialog-footer">
        <button @click="$emit('close')" class="cancel-button">
          Cancel
        </button>
        <button 
          @click="confirmRestrictions" 
          class="save-button"
        >
          Save Restrictions
        </button>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'LessonsGeoRestrictionsDialog',
  props: {
    show: {
      type: Boolean,
      required: true
    },
    selectedLessons: {
      type: Array,
      default: () => []
    },
    currentRestrictions: {
      type: Object,
      default: () => ({
        enabled: false,
        restrictionType: 'blocklist',
        countries: [],
        ipRanges: [],
        blockMessage: 'This content is not available in your region.'
      })
    }
  },
  data() {
    return {
      form: {
        enabled: false,
        restrictionType: 'blocklist',
        countries: [],
        ipRanges: [],
        blockMessage: 'This content is not available in your region.'
      },
      availableCountries: [
        { code: 'US', name: 'United States' },
        { code: 'CA', name: 'Canada' },
        { code: 'MX', name: 'Mexico' },
        { code: 'GB', name: 'United Kingdom' },
        { code: 'FR', name: 'France' },
        { code: 'DE', name: 'Germany' },
        { code: 'JP', name: 'Japan' },
        { code: 'CN', name: 'China' },
        { code: 'IN', name: 'India' },
        { code: 'BR', name: 'Brazil' },
        { code: 'AU', name: 'Australia' },
        { code: 'RU', name: 'Russia' },
        // Add more countries as needed
      ]
    }
  },
  watch: {
    currentRestrictions: {
      handler(newRestrictions) {
        if (newRestrictions) {
          this.form = JSON.parse(JSON.stringify(newRestrictions));
        }
      },
      immediate: true
    }
  },
  methods: {
    addIpRange() {
      this.form.ipRanges.push({ start: '', end: '' });
    },
    removeIpRange(index) {
      this.form.ipRanges.splice(index, 1);
    },
    confirmRestrictions() {
      this.$emit('save', this.form);
    }
  }
}
</script>

<style scoped>
.dialog-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 50;
}

.dialog-container {
  background: white;
  border-radius: 12px;
  width: 100%;
  max-width: 640px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
}

.dialog-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1.5rem;
  border-bottom: 1px solid #e2e8f0;
}

.dialog-title {
  font-size: 1.25rem;
  font-weight: 600;
  color: #1e293b;
  margin: 0;
}

.close-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: none;
  border-radius: 6px;
  background: #f1f5f9;
  color: #475569;
  cursor: pointer;
  transition: all 0.2s ease;
}

.close-button:hover {
  background: #e2e8f0;
}

.close-icon {
  width: 16px;
  height: 16px;
}

.dialog-content {
  padding: 1.5rem;
  overflow-y: auto;
}

.selected-count {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 1.5rem;
  padding: 0.75rem;
  background: #f8fafc;
  border-radius: 6px;
}

.count {
  font-size: 1.125rem;
  font-weight: 600;
  color: #3b82f6;
}

.text {
  font-size: 0.875rem;
  color: #64748b;
}

.info-section {
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  padding: 1rem;
  background: #f8fafc;
  border-radius: 8px;
  margin-bottom: 1.5rem;
}

.info-icon {
  width: 24px;
  height: 24px;
  color: #3b82f6;
  flex-shrink: 0;
}

.info-text {
  font-size: 0.875rem;
  color: #475569;
  margin: 0;
  line-height: 1.5;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  margin-bottom: 1.5rem;
}

.form-label {
  font-size: 0.875rem;
  font-weight: 500;
  color: #1e293b;
}

.toggle-container {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.toggle-label {
  font-size: 0.875rem;
  font-weight: 500;
  color: #1e293b;
}

.switch {
  position: relative;
  display: inline-block;
  width: 48px;
  height: 24px;
}

.switch input {
  opacity: 0;
  width: 0;
  height: 0;
}

.slider {
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: #e2e8f0;
  transition: .4s;
  border-radius: 24px;
}

.slider:before {
  position: absolute;
  content: "";
  height: 18px;
  width: 18px;
  left: 3px;
  bottom: 3px;
  background-color: white;
  transition: .4s;
  border-radius: 50%;
}

input:checked + .slider {
  background-color: #3b82f6;
}

input:focus + .slider {
  box-shadow: 0 0 1px #3b82f6;
}

input:checked + .slider:before {
  transform: translateX(24px);
}

.radio-group {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.radio-label {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.875rem;
  color: #475569;
  cursor: pointer;
}

.radio-label input {
  cursor: pointer;
}

.form-select {
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  font-size: 0.875rem;
  color: #1e293b;
  background: white;
  transition: all 0.2s ease;
}

.form-select:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.country-select .form-select {
  height: 150px;
}

.helper-text {
  font-size: 0.75rem;
  color: #64748b;
  margin: 0.5rem 0 0;
}

.ip-ranges {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.ip-range-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.ip-input {
  flex: 1;
}

.ip-separator {
  font-size: 0.875rem;
  color: #64748b;
}

.form-input,
.form-textarea {
  padding: 0.75rem;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  font-size: 0.875rem;
  color: #1e293b;
  background: white;
  transition: all 0.2s ease;
}

.form-input:focus,
.form-textarea:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.form-textarea {
  resize: vertical;
  min-height: 80px;
  width: 100%;
}

.remove-ip-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border: none;
  border-radius: 6px;
  background: #fee2e2;
  color: #b91c1c;
  cursor: pointer;
  transition: all 0.2s ease;
}

.remove-ip-button:hover {
  background: #fecaca;
}

.remove-icon {
  width: 14px;
  height: 14px;
}

.add-ip-button {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  border: 1px dashed #e2e8f0;
  border-radius: 6px;
  background: #f8fafc;
  color: #3b82f6;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
}

.add-ip-button:hover {
  background: #f1f5f9;
  border-color: #cbd5e1;
}

.add-icon {
  width: 16px;
  height: 16px;
}

.dialog-footer {
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
  padding: 1.5rem;
  border-top: 1px solid #e2e8f0;
  margin-top: auto;
}

.cancel-button {
  padding: 0.75rem 1.25rem;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  background: white;
  color: #475569;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
}

.cancel-button:hover {
  background: #f8fafc;
}

.save-button {
  padding: 0.75rem 1.25rem;
  border: none;
  border-radius: 6px;
  background: #3b82f6;
  color: white;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
}

.save-button:hover {
  background: #2563eb;
}

@media (max-width: 640px) {
  .dialog-container {
    margin: 1rem;
    max-height: calc(100vh - 2rem);
  }
  
  .ip-range-item {
    flex-direction: column;
    align-items: stretch;
  }
  
  .ip-separator {
    text-align: center;
    margin: 0.25rem 0;
  }
  
  .remove-ip-button {
    align-self: flex-end;
  }
}
</style> 